import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase, checkSupabaseConfig } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import LegalNotice from '../../components/LegalNotice';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AdvisorFirmInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshProfile, setMissingProfile } = useAuth();

  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const completeInvitation = async () => {
    const { data, error: rpcError } = await supabase.rpc('complete_advisor_firm_invitation', {
      p_token: token
    });

    if (rpcError) throw rpcError;

    await refreshProfile();
    setMissingProfile(false);

    const result = Array.isArray(data) ? data[0] : data;
    navigate(result?.route_target || '/dashboard', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);

    const config = checkSupabaseConfig();
    if (!config.valid) {
      setError(config.error);
      setLoading(false);
      return;
    }

    if (!token) {
      setError('Invitation token is missing.');
      setLoading(false);
      return;
    }

    try {
      if (mode === 'signin') {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) throw signInError;
        await completeInvitation();
        return;
      }

      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email,
        password
      });

      if (signupError) throw signupError;

      if (!signupData.session) {
        setNotice('Check your email to confirm your account, then return to this invitation link to finish setup.');
        return;
      }

      await completeInvitation();
    } catch (err) {
      console.error('Advisor firm invitation error:', err);
      setError(err.message || 'Unable to complete invitation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between">
      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <SafeIcon icon={FiIcons.FiTrendingUp} className="mx-auto h-12 w-12 text-slate-800" />
          <h2 className="mt-6 text-3xl font-extrabold text-slate-900 tracking-tight">
            Advisor Portal Invitation
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Create or sign in to your account using the invited email address.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 border border-slate-200">
            <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-md mb-6">
              <button
                type="button"
                onClick={() => setMode('signup')}
                className={`py-2 text-sm font-medium rounded transition-colors ${
                  mode === 'signup' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                Create Account
              </button>
              <button
                type="button"
                onClick={() => setMode('signin')}
                className={`py-2 text-sm font-medium rounded transition-colors ${
                  mode === 'signin' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                Sign In
              </button>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                  <div className="flex">
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2 shrink-0" />
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                </div>
              )}

              {notice && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                  <div className="flex">
                    <SafeIcon icon={FiIcons.FiMail} className="h-5 w-5 text-blue-500 mr-2 shrink-0" />
                    <p className="text-sm text-blue-700 font-medium">{notice}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700">Email address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 focus:ring-slate-500 focus:border-slate-500 block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border shadow-sm"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <span className="flex items-center">
                    <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                    Completing Setup...
                  </span>
                ) : mode === 'signin' ? 'Sign In & Accept Invitation' : 'Create Account & Accept Invitation'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900">
                Back to standard sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
      <LegalNotice />
    </div>
  );
};

export default AdvisorFirmInvitation;
